import React, { useEffect, useState } from 'react'
import { IntersectionOptions, useInView } from 'react-intersection-observer'
import { FluidObject } from 'gatsby-image'
import styled from '@emotion/styled'

import { breakpoints } from '../styles/variables'
import { FluidImage } from './FluidImage'

const StyledPhilosophyBlock = styled.div<{ active: boolean }>`
  display: grid;
  grid-template-columns: 6rem 1fr 2rem;
  grid-template-rows: auto 2rem auto;
  opacity: ${({ active }) => (active ? 1 : 0)};
  transition: opacity 0.5s;

  @media (min-width: ${breakpoints.tablet}px) {
    background: var(--color-figure-0025);
    grid-template-columns: 1fr minmax(22rem, 2fr) 1fr 50%;
    grid-template-rows: 1fr auto 1rem auto 1fr;
  }
`

const Title = styled.h2`
  font-family: var(--font-family-serif);
  font-size: 2rem;
  font-weight: normal;
  grid-column: 1;
  grid-row: 1 / 4;
  justify-self: center;
  margin: 0;
  text-orientation: mixed;
  white-space: nowrap;
  writing-mode: vertical-rl;

  &:lang(en) {
    font-family: var(--font-family-display);
    font-size: 1.68rem;
    letter-spacing: 0.15em;
    text-transform: uppercase;
  }

  @media (min-width: ${breakpoints.tablet}px) {
    font-feature-settings: 'palt';
    grid-column: 2;
    grid-row: 2;
    justify-self: start;
    writing-mode: horizontal-tb;

    &:lang(en) {
      font-family: var(--font-family-serif);
      font-size: 2rem;
      letter-spacing: 0;
      text-transform: none;
    }
  }
`

const Content = styled.div`
  font-family: var(--font-family-serif);
  font-feature-settings: 'palt';
  grid-column: 2;
  grid-row: 3;
  letter-spacing: 0.1em;
  line-height: 2;

  p:first-child {
    margin-top: 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  &:lang(en) {
    letter-spacing: 0.05em;
    line-height: 1.75;
  }

  @media (min-width: ${breakpoints.tablet}px) {
    grid-column: 2;
    grid-row: 4;
  }
`

const ImageContainer = styled.div`
  grid-column: 2 / 4;
  grid-row: 1;

  @media (min-width: ${breakpoints.tablet}px) {
    grid-column: 4;
    grid-row: 1 / 6;
  }
`

const useInViewOnce = (option: IntersectionOptions) => {
  const [ref, inView] = useInView(option)
  const [active, setActive] = useState(false)
  useEffect(() => {
    if (inView) setActive(true)
  }, [inView])
  return { ref, active }
}

interface PhilosophyBlockProps {
  title: string
  fluid: FluidObject
}

export const PhilosophyBlock: React.FC<PhilosophyBlockProps> = ({
  children,
  title,
  fluid,
}) => {
  const { ref, active } = useInViewOnce({ threshold: 0.25 })

  return (
    <StyledPhilosophyBlock ref={ref} active={active}>
      <Title>{title}</Title>
      <Content>{children}</Content>
      <ImageContainer>
        <FluidImage
          fluid={fluid}
          sizes={`(min-width: ${breakpoints.tablet}px) 50vw, 76vw`}
        />
      </ImageContainer>
    </StyledPhilosophyBlock>
  )
}
